import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { engineers, type Engineer } from '@/mocks/talentPool';
import RequestMatchModal from './RequestMatchModal';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const pillStagger = {
  hidden: { opacity: 0, y: 12, scale: 0.94 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.04,
      duration: 0.35,
      ease: 'easeOut',
    },
  }),
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.94,
    transition: { duration: 0.2 },
  },
};

interface SkillCategory {
  id: string;
  label: string;
  icon: string;
  skills: string[];
}

const skillCategories: SkillCategory[] = [
  {
    id: 'frontend',
    label: 'Frontend',
    icon: 'ri-layout-masonry-line',
    skills: ['React', 'Vue.js', 'TypeScript', 'Next.js', 'GraphQL'],
  },
  {
    id: 'backend',
    label: 'Backend',
    icon: 'ri-server-line',
    skills: ['Node.js', 'Python', 'Java', '.NET'],
  },
  {
    id: 'cloud',
    label: 'Cloud & DevOps',
    icon: 'ri-cloud-line',
    skills: ['AWS', 'Azure', 'Kubernetes', 'Docker', 'Terraform', 'CI/CD'],
  },
  {
    id: 'data',
    label: 'Data & Storage',
    icon: 'ri-database-2-line',
    skills: ['PostgreSQL', 'MongoDB', 'Spark', 'Snowflake', 'Kafka'],
  },
  {
    id: 'ai',
    label: 'AI & ML',
    icon: 'ri-brain-line',
    skills: ['PyTorch', 'TensorFlow'],
  },
  {
    id: 'qa',
    label: 'QA & Testing',
    icon: 'ri-bug-line',
    skills: ['Cypress', 'Playwright'],
  },
  {
    id: 'mobile',
    label: 'Mobile',
    icon: 'ri-smartphone-line',
    skills: ['Swift'],
  },
];

interface MatchResult {
  engineer: Engineer;
  matchScore: number;
  matchedSkills: string[];
  missingSkills: string[];
}

const scanMessages = [
  'Scanning 482 engineers...',
  'Analyzing skill overlap...',
  'Calculating match scores...',
  'Ranking top candidates...',
];

export default function SkillsMatcher() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('frontend');
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanMessageIndex, setScanMessageIndex] = useState(0);
  const [showResults, setShowResults] = useState(false);

  // Compare mode state
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState<Engineer | null>(null);

  const openModal = (engineer: Engineer) => {
    setSelectedEngineer(engineer);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedEngineer(null), 300);
  };

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const clearSkills = () => {
    setSelectedSkills([]);
    setShowResults(false);
    setCompareIds([]);
  };

  const toggleCompare = (id: number) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const clearCompare = () => setCompareIds([]);

  const currentCategorySkills =
    skillCategories.find((c) => c.id === activeCategory)?.skills || [];

  const matches = useMemo(() => {
    if (selectedSkills.length === 0) return [];

    const results: MatchResult[] = engineers.map((engineer) => {
      const matchedSkills = selectedSkills.filter((skill) =>
        engineer.skills.includes(skill)
      );
      const missingSkills = selectedSkills.filter(
        (skill) => !engineer.skills.includes(skill)
      );
      const matchScore = Math.round(
        (matchedSkills.length / selectedSkills.length) * 100
      );

      return {
        engineer,
        matchScore,
        matchedSkills,
        missingSkills,
      };
    });

    return results
      .filter((r) => r.matchScore > 0)
      .sort((a, b) => b.matchScore - a.matchScore);
  }, [selectedSkills]);

  const compareEngineers = useMemo(() => {
    return matches.filter((m) => compareIds.includes(m.engineer.id));
  }, [matches, compareIds]);

  const handleFindMatches = () => {
    if (selectedSkills.length === 0) return;
    setIsScanning(true);
    setScanProgress(0);
    setScanMessageIndex(0);

    let progress = 0;
    let msgIndex = 0;
    const totalDuration = 1400;
    const intervalMs = 40;
    const steps = totalDuration / intervalMs;
    const progressStep = 100 / steps;

    const interval = setInterval(() => {
      progress += progressStep;
      if (progress >= 100) {
        progress = 100;
      }
      setScanProgress(progress);

      const newMsgIndex = Math.min(
        Math.floor((progress / 100) * scanMessages.length),
        scanMessages.length - 1
      );
      if (newMsgIndex !== msgIndex) {
        msgIndex = newMsgIndex;
        setScanMessageIndex(msgIndex);
      }

      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          setIsScanning(false);
          setShowResults(true);
          const el = document.getElementById('matcher-results');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }, intervalMs);
  };

  const suggestedStacks = [
    { label: 'Full-Stack React', skills: ['React', 'Node.js', 'TypeScript', 'PostgreSQL'] },
    { label: 'Cloud Native', skills: ['Kubernetes', 'Docker', 'AWS', 'CI/CD'] },
    { label: 'AI/ML Pipeline', skills: ['Python', 'PyTorch', 'Spark', 'Kafka'] },
    { label: 'Mobile + Backend', skills: ['Swift', 'React', 'Node.js', 'Firebase'] },
  ];

  const applyStack = (skills: string[]) => {
    setSelectedSkills(skills);
    setShowResults(false);
    setCompareIds([]);
  };

  const [recalcKey, setRecalcKey] = useState(0);
  const handleSkillToggleWithRecalc = (skill: string) => {
    toggleSkill(skill);
    if (showResults && selectedSkills.length > 0) {
      setRecalcKey((k) => k + 1);
    }
  };

  return (
    <section
      id="skills-matcher"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-magic-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">AI-Powered Matching</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Skills
            <br />
            <span className="text-primary-600">Matcher</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Tell us what your team is building. We will instantly scan our talent pool and surface the best-matched engineers for your stack.
          </motion.p>
        </motion.div>

        {/* Suggested Stacks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-8 md:mb-10"
        >
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center mb-3">
            Quick-start with a common stack
          </p>
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {suggestedStacks.map((stack) => (
              <button
                key={stack.label}
                onClick={() => applyStack(stack.skills)}
                className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-xs md:text-sm font-medium text-gray-700 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-stack-line mr-1.5 text-gray-400"></i>
                {stack.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Selected Skills Bar */}
        <AnimatePresence>
          {selectedSkills.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-navy-900 rounded-2xl p-4 md:p-5 mb-6 md:mb-8"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                    <i className="ri-stack-line text-primary-400 text-sm"></i>
                  </div>
                  <span className="text-sm font-semibold text-white">
                    Your Stack — {selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  onClick={clearSkills}
                  className="text-xs font-medium text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedSkills.map((skill) => (
                  <span
                    key={skill}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm text-white"
                  >
                    {skill}
                    <button
                      onClick={() => handleSkillToggleWithRecalc(skill)}
                      className="w-4 h-4 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-5 md:mb-6"
        >
          {skillCategories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.label}
              </button>
            );
          })}
        </motion.div>

        {/* Skill Pills */}
        <motion.div
          key={activeCategory}
          initial="hidden"
          animate="show"
          exit="exit"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.04 } },
            exit: { transition: { staggerChildren: 0.02 } },
          }}
          className="bg-gray-50/80 border border-gray-100 rounded-2xl p-5 md:p-6 mb-6 md:mb-8"
        >
          <div className="flex flex-wrap gap-2 md:gap-2.5">
            <AnimatePresence mode="popLayout">
              {currentCategorySkills.map((skill, i) => {
                const isSelected = selectedSkills.includes(skill);
                return (
                  <motion.button
                    key={skill}
                    layout
                    custom={i}
                    variants={pillStagger}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    onClick={() => handleSkillToggleWithRecalc(skill)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                      isSelected
                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {isSelected && (
                      <i className="ri-check-line mr-1.5 text-xs"></i>
                    )}
                    {skill}
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Find Matches Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.45, duration: 0.5 }}
          className="text-center mb-6 md:mb-8"
        >
          <button
            onClick={handleFindMatches}
            disabled={selectedSkills.length === 0 || isScanning}
            className={`px-10 py-4 rounded-xl text-base font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap group inline-flex items-center gap-2 ${
              selectedSkills.length > 0 && !isScanning
                ? 'bg-primary-500 text-white hover:shadow-xl hover:shadow-primary-500/20 hover:-translate-y-0.5 hover:brightness-110'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isScanning ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg"></i>
                Matching...
              </>
            ) : (
              <>
                <i className="ri-search-line text-lg"></i>
                Find Matches
                {selectedSkills.length > 0 && (
                  <span className="ml-1 text-sm font-normal opacity-80">
                    ({selectedSkills.length} skill{selectedSkills.length !== 1 ? 's' : ''})
                  </span>
                )}
              </>
            )}
          </button>

          {selectedSkills.length === 0 && (
            <p className="text-xs text-gray-400 mt-3">
              Select at least one skill above to find matching engineers
            </p>
          )}
        </motion.div>

        {/* Scanning Overlay */}
        <AnimatePresence>
          {isScanning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-gray-50/90 border border-gray-100 rounded-2xl p-8 md:p-12 mb-8 text-center backdrop-blur-sm"
            >
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-primary-50 rounded-full">
                  <i className="ri-radar-line text-3xl text-primary-500 animate-pulse"></i>
                </div>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={scanMessageIndex}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="text-sm font-medium text-navy-900 mb-4"
                  >
                    {scanMessages[scanMessageIndex]}
                  </motion.p>
                </AnimatePresence>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-primary-500 rounded-full"
                    style={{ width: `${scanProgress}%` }}
                    transition={{ duration: 0.05 }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  Scanning {engineers.length} pre-vetted engineers across{' '}
                  {new Set(engineers.map((e) => e.timezone)).size} timezones
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Live Recalculation Pulse */}
        <AnimatePresence>
          {showResults && selectedSkills.length > 0 && recalcKey > 0 && (
            <motion.div
              key={recalcKey}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="text-center mb-4"
            >
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-100 rounded-full text-xs font-medium text-primary-600">
                <i className="ri-refresh-line animate-spin text-sm"></i>
                Recalculating matches...
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results with Compare */}
        <AnimatePresence mode="popLayout">
          {showResults && selectedSkills.length > 0 && matches.length > 0 && (
            <motion.div
              id="matcher-results"
              key={`results-${selectedSkills.join('-')}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center justify-between mb-5 md:mb-6">
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-navy-900">
                    Top Matches
                  </h3>
                  <p className="text-sm text-gray-500">
                    {matches.length} engineer{matches.length !== 1 ? 's' : ''} match your stack
                  </p>
                </div>
                <button
                  onClick={clearSkills}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  Start Over
                </button>
              </div>

              <motion.div
                layout
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
              >
                <AnimatePresence mode="popLayout">
                  {matches.map((match, index) => {
                    const { engineer, matchScore, matchedSkills, missingSkills } = match;
                    const isCompared = compareIds.includes(engineer.id);
                    return (
                      <motion.div
                        key={engineer.id}
                        layout
                        layoutId={`card-${engineer.id}`}
                        initial={{ opacity: 0, y: 20, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.25 } }}
                        transition={{
                          layout: { type: 'spring', stiffness: 350, damping: 30 },
                          opacity: { duration: 0.35, delay: Math.min(index * 0.06, 0.3) },
                          scale: { duration: 0.35, delay: Math.min(index * 0.06, 0.3) },
                        }}
                        className={`bg-white border rounded-2xl p-5 md:p-6 hover:border-primary-200 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-300 ${
                          isCompared ? 'border-primary-400 shadow-md shadow-primary-500/8' : 'border-gray-100'
                        }`}
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-11 h-11 rounded-xl ${engineer.avatarColor} flex items-center justify-center flex-shrink-0`}
                            >
                              <span className="text-white text-sm font-bold">
                                {engineer.initials}
                              </span>
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-navy-900">
                                {engineer.name}
                              </h4>
                              <p className="text-xs text-gray-500">{engineer.role}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <motion.p
                              key={`score-${matchScore}-${engineer.id}`}
                              initial={{ scale: 0.8 }}
                              animate={{ scale: 1 }}
                              transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                              className="text-2xl font-bold text-primary-600"
                            >
                              {matchScore}%
                            </motion.p>
                            <p className="text-[10px] text-gray-400">match</p>
                          </div>
                        </div>

                        {/* Match Score Bar */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="text-gray-500">
                              {matchedSkills.length} of {selectedSkills.length} skills
                            </span>
                            <span className="font-medium text-primary-600">
                              {matchScore}%
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                              key={`bar-${matchScore}-${engineer.id}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${matchScore}%` }}
                              transition={{
                                delay: Math.min(index * 0.06, 0.3) + 0.15,
                                duration: 0.7,
                                ease: 'easeOut',
                              }}
                              className="h-full bg-primary-500 rounded-full"
                            />
                          </div>
                        </div>

                        {/* Matched Skills */}
                        <div className="mb-3">
                          <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1.5">
                            Matched Skills
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {matchedSkills.map((skill) => (
                              <span
                                key={skill}
                                className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] md:text-xs font-medium text-emerald-700"
                              >
                                <i className="ri-check-line mr-0.5 text-[10px]"></i>
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Missing Skills */}
                        {missingSkills.length > 0 && (
                          <div className="mb-4">
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                              Missing Skills
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {missingSkills.map((skill) => (
                                <span
                                  key={skill}
                                  className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[10px] md:text-xs font-medium text-gray-400"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Meta */}
                        <div className="flex items-center gap-3 mb-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {engineer.years}yr
                          </span>
                          <span className="text-gray-300">·</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-map-pin-time-line"></i>
                            {engineer.timezoneOffset}
                          </span>
                          <span className="text-gray-300">·</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-money-dollar-circle-line"></i>
                            ${engineer.rateMin}–${engineer.rateMax}/hr
                          </span>
                        </div>

                        {/* Actions row */}
                        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => toggleCompare(engineer.id)}
                            disabled={!isCompared && compareIds.length >= 3}
                            className={`flex-1 py-2.5 rounded-xl text-xs md:text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                              isCompared
                                ? 'bg-primary-500 text-white hover:bg-primary-600'
                                : compareIds.length >= 3
                                ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                : 'bg-gray-50 text-navy-900 border border-gray-200 hover:border-primary-300 hover:text-primary-600'
                            }`}
                          >
                            {isCompared ? (
                              <>
                                <i className="ri-checkbox-circle-line"></i>
                                Selected
                              </>
                            ) : (
                              <>
                                <i className="ri-add-circle-line"></i>
                                Compare
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => openModal(engineer)}
                            className="flex-1 py-2.5 bg-navy-900 text-white text-xs md:text-sm font-semibold rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
                          >
                            Request Match
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>

              {/* Bottom CTA */}
              <div className="mt-8 md:mt-10 text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Want a human recruiter to refine these results?
                </p>
                <button
                  onClick={() => {
                    const el = document.getElementById('cta');
                    if (el) {
                      const offset = 80;
                      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                      window.scrollTo({ top: pos, behavior: 'smooth' });
                    }
                  }}
                  className="px-8 py-3.5 bg-navy-900 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                >
                  Talk to a Recruiter
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* No Results */}
        <AnimatePresence>
          {showResults && selectedSkills.length > 0 && matches.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-center py-12 md:py-16 bg-gray-50/50 border border-gray-100 rounded-2xl"
            >
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
                <i className="ri-search-2-line text-2xl text-gray-400"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">
                No exact matches found
              </h3>
              <p className="text-sm text-gray-500 mb-2 max-w-md mx-auto">
                None of our current engineers have all of those skills together. But our recruiters source new talent weekly.
              </p>
              <p className="text-xs text-gray-400 mb-6">
                Try removing a skill or two, or submit a custom request.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={clearSkills}
                  className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all cursor-pointer"
                >
                  Adjust Your Stack
                </button>
                <button
                  onClick={() => {
                    const el = document.getElementById('cta');
                    if (el) {
                      const offset = 80;
                      const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                      window.scrollTo({ top: pos, behavior: 'smooth' });
                    }
                  }}
                  className="px-6 py-2.5 border-2 border-gray-200 text-navy-900 text-sm font-medium rounded-lg hover:border-primary-400 transition-colors cursor-pointer"
                >
                  Submit Custom Request
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compare floating bar */}
        <AnimatePresence>
          {compareIds.length >= 2 && !compareOpen && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-full max-w-md px-4"
            >
              <div className="bg-white border border-gray-200 shadow-xl shadow-black/10 rounded-2xl p-4 flex items-center gap-3">
                <div className="flex -space-x-2 flex-shrink-0">
                  {compareEngineers.map((m) => (
                    <div
                      key={m.engineer.id}
                      className={`w-9 h-9 rounded-full ${m.engineer.avatarColor} border-2 border-white flex items-center justify-center flex-shrink-0`}
                    >
                      <span className="text-white text-[10px] font-bold">
                        {m.engineer.initials}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy-900">
                    {compareIds.length} engineers selected
                  </p>
                  <p className="text-xs text-gray-500">
                    Compare side-by-side
                  </p>
                </div>
                <button
                  onClick={() => setCompareOpen(true)}
                  className="px-4 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-md transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  <i className="ri-bar-chart-grouped-line mr-1.5"></i>
                  Compare
                </button>
                <button
                  onClick={clearCompare}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compare Panel Modal */}
        <AnimatePresence>
          {compareOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-50 bg-navy-900/50 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setCompareOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 10 }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                className="bg-white rounded-2xl md:rounded-3xl w-full max-w-5xl max-h-[85vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Panel Header */}
                <div className="sticky top-0 bg-white z-10 px-6 md:px-8 pt-6 md:pt-8 pb-4 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-navy-900 flex items-center gap-2">
                      <i className="ri-bar-chart-grouped-line text-primary-600"></i>
                      Side-by-Side Comparison
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Based on your {selectedSkills.length} selected skills
                    </p>
                  </div>
                  <button
                    onClick={() => setCompareOpen(false)}
                    className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-navy-900 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                {/* Comparison Grid */}
                <div className="p-6 md:p-8">
                  <div
                    className="grid gap-4 md:gap-6"
                    style={{
                      gridTemplateColumns: `repeat(${compareEngineers.length}, minmax(220px, 1fr))`,
                    }}
                  >
                    {compareEngineers.map((match) => {
                      const { engineer, matchScore, matchedSkills, missingSkills } = match;
                      return (
                        <div
                          key={engineer.id}
                          className="border border-gray-100 rounded-2xl overflow-hidden bg-white min-w-0"
                        >
                          {/* Profile Header */}
                          <div className="bg-gray-50/60 p-5 md:p-6 text-center">
                            <div
                              className={`w-14 h-14 mx-auto mb-3 rounded-2xl ${engineer.avatarColor} flex items-center justify-center`}
                            >
                              <span className="text-white text-lg font-bold">
                                {engineer.initials}
                              </span>
                            </div>
                            <h4 className="text-base font-bold text-navy-900">
                              {engineer.name}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {engineer.role}
                            </p>
                            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-primary-50 border border-primary-100 rounded-full">
                              <i className="ri-focus-3-line text-primary-500 text-xs"></i>
                              <span className="text-sm font-bold text-primary-600">
                                {matchScore}% match
                              </span>
                            </div>
                          </div>

                          <div className="p-5 md:p-6 space-y-5">
                            {/* Match Score Bar */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                Match Score
                              </p>
                              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${matchScore}%` }}
                                  transition={{ duration: 0.8, ease: 'easeOut' }}
                                  className="h-full bg-primary-500 rounded-full"
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {matchedSkills.length} of {selectedSkills.length} skills
                              </p>
                            </div>

                            {/* Seniority */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                Seniority
                              </p>
                              <div className="flex items-center gap-2">
                                <span className="px-2.5 py-1 bg-navy-50 border border-navy-100 rounded-lg text-xs font-semibold text-navy-700">
                                  {engineer.seniority}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {engineer.years} years exp
                                </span>
                              </div>
                            </div>

                            {/* Rate */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                Rate
                              </p>
                              <p className="text-sm font-bold text-navy-900">
                                ${engineer.rateMin}–${engineer.rateMax}
                                <span className="text-xs font-normal text-gray-400"> /hr</span>
                              </p>
                            </div>

                            {/* Timezone */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                Timezone
                              </p>
                              <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                <i className="ri-map-pin-time-line text-gray-400 text-xs"></i>
                                {engineer.timezone}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5 ml-5">
                                Offset {engineer.timezoneOffset}
                              </p>
                            </div>

                            {/* Languages */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                Languages
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {engineer.languages.map((lang) => (
                                  <span
                                    key={lang}
                                    className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-xs font-medium text-gray-600"
                                  >
                                    {lang}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Matched Skills */}
                            <div>
                              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">
                                Matched Skills
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {matchedSkills.map((skill) => (
                                  <span
                                    key={skill}
                                    className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-xs font-medium text-emerald-700"
                                  >
                                    <i className="ri-check-line mr-0.5 text-[10px]"></i>
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Missing Skills */}
                            {missingSkills.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                  Missing Skills
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {missingSkills.map((skill) => (
                                    <span
                                      key={skill}
                                      className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-xs font-medium text-gray-400"
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Highlight */}
                            {engineer.highlight && (
                              <div className="flex items-start gap-2 bg-primary-50/40 border border-primary-100 rounded-xl p-3">
                                <i className="ri-star-smile-line text-primary-500 text-xs mt-0.5 flex-shrink-0"></i>
                                <p className="text-xs text-primary-700 leading-relaxed">
                                  {engineer.highlight}
                                </p>
                              </div>
                            )}

                            {/* Status */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                Status
                              </p>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                  engineer.status === 'Available Now'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : engineer.status === 'Interviewing'
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-gray-50 text-gray-600 border-gray-200'
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    engineer.status === 'Available Now'
                                      ? 'bg-emerald-500'
                                      : engineer.status === 'Interviewing'
                                      ? 'bg-amber-500'
                                      : 'bg-gray-400'
                                  }`}
                                ></span>
                                {engineer.status}
                              </span>
                            </div>

                            {/* CTA */}
                            <button
                              onClick={() => {
                                setCompareOpen(false);
                                openModal(engineer);
                              }}
                              className="w-full py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-md hover:shadow-primary-500/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
                            >
                              Request Match
                              <i className="ri-arrow-right-line ml-1.5 group-hover:translate-x-0.5 transition-transform inline-block"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Request Match Modal */}
        <RequestMatchModal
          engineer={selectedEngineer}
          isOpen={modalOpen}
          onClose={closeModal}
        />
      </div>
    </section>
  );
}